import React, { useEffect, useState } from 'react';
import { Button, Table, Space, message, Spin, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EditOutlined, DeleteOutlined, UploadOutlined, PlusOutlined } from '@ant-design/icons';
import { useAnnonceApi } from '../../../api/annonce/annonces';
import AppLayout from '../../../components/layout/AppLayout';
import ConfirmationDialog from '../../../components/layout/ConfirmationDialog';
import { TAnnonce } from '../../../models/annonce/annonce';
import AnnonceBannerForm from '../../../components/AdvertBanner/AnnonceBannerForm';
import { getImagePath } from '../../../utils/format/valueFormat';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import './AnnonceList.css';

const { Title } = Typography;

const palette = {
    primary: '#2992BF',
    secondary: '#8BBE1E',
    border: '#e0e7f1',
    textPrimary: '#0f172a',
    textMuted: '#6b7280',
    surface: '#ffffff',
};

const AnnonceList: React.FC = () => {
    const { getAllAnnonces, deleteAnnonce, uploadAnnonceImage } = useAnnonceApi();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [annonces, setAnnonces] = useState<TAnnonce[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingRow, setLoadingRow] = useState<number | null>(null);

    const [isDeleteDialogVisible, setIsDeleteDialogVisible] = useState<boolean>(false);
    const [deleteId, setDeleteId] = useState<number | null>(null);
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [currentAnnonce, setCurrentAnnonce] = useState<TAnnonce | null>(null);

    useEffect(() => {
        setLoading(true);
        getAllAnnonces()
            .then((resp) => setAnnonces(resp.data))
            .catch((err) => message.error(err.response?.data ?? err.message))
            .finally(() => setLoading(false));
        // eslint-disable-next-line
    }, []);

    const handleCreateAnnonce = () => {
        setIsEditMode(false);
        setCurrentAnnonce(null);
        setIsModalVisible(true);
    };

    const handleEditAnnonce = (annonce: TAnnonce) => {
        setIsEditMode(true);
        setCurrentAnnonce(annonce);
        setIsModalVisible(true);
    };

    const confirmDelete = (id: number) => {
        setDeleteId(id);
        setIsDeleteDialogVisible(true);
    };

    const handleDelete = () => {
        if (deleteId == null) return;

        setDeleteLoading(true);
        deleteAnnonce(deleteId)
            .then(() => {
                setAnnonces((prev) => prev.filter((item) => item.id !== deleteId));
                message.success('Annonce supprimée avec succès.');
            })
            .catch((err) => message.error(err.response?.data ?? err.message))
            .finally(() => {
                setDeleteLoading(false);
                setIsDeleteDialogVisible(false);
                setDeleteId(null);
            });
    };

    const uploadImage = (annonce: TAnnonce) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';

        input.onchange = async (event: any) => {
            const file = event.target.files[0];
            if (file) {
                setLoadingRow(annonce.id);
                const formData = new FormData();
                formData.append('image', file);

                uploadAnnonceImage(annonce.id, formData)
                    .then((resp) => {
                        const updatedAnnonce = resp.data;
                        setAnnonces((prev) =>
                            prev.map((item) => (item.id === annonce.id ? updatedAnnonce : item))
                        );
                        message.success('Image mise à jour avec succès.');
                    })
                    .catch((err) => message.error(err.response?.data ?? err.message))
                    .finally(() => setLoadingRow(null));
            }
        };

        input.click();
    };

    const columns: ColumnsType<TAnnonce> = [
        {
            title: t('admin.advert.tableHeaders.image'),
            dataIndex: 'imageUrl',
            key: 'imageUrl',
            width: 100,
            render: (_: string, record: TAnnonce) =>
                record.image_url ? (
                    <div
                        style={{
                            width: 64,
                            height: 64,
                            borderRadius: 14,
                            overflow: 'hidden',
                            border: `2px solid ${palette.border}`,
                            boxShadow: '0 4px 12px rgba(15,23,42,0.08)',
                            background: 'rgba(244,248,251,0.6)',
                        }}
                    >
                        <img
                            src={getImagePath(record.image_url, true)}
                            alt="annonce-thumbnail"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    </div>
                ) : (
                    <div
                        style={{
                            width: 64,
                            height: 64,
                            borderRadius: 14,
                            background: 'linear-gradient(135deg, rgba(41,146,191,0.1), rgba(139,190,30,0.08))',
                            border: `2px dashed ${palette.border}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: palette.textMuted,
                            fontSize: '0.75rem',
                        }}
                    >
                        N/A
                    </div>
                ),
        },
        {
            title: t('admin.advert.tableHeaders.title'),
            dataIndex: 'title',
            key: 'title',
            width: 220,
            render: (text: string) => (
                <span style={{ fontWeight: 600, color: palette.textPrimary }}>{text}</span>
            ),
        },
        {
            title: t('admin.advert.tableHeaders.description'),
            dataIndex: 'description',
            key: 'description',
            width: 320,
            render: (text: string) => (
                <span
                    style={{
                        color: palette.textMuted,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {text || '-'}
                </span>
            ),
        },
        {
            title: t('admin.advert.tableHeaders.backgroundColor'),
            dataIndex: 'background_color',
            key: 'background_color',
            width: 120,
            render: (color: string) => (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                    }}
                >
                    <div
                        style={{
                            backgroundColor: color || palette.primary,
                            width: 48,
                            height: 28,
                            borderRadius: 12,
                            border: `2px solid ${palette.border}`,
                            boxShadow: '0 4px 12px rgba(15,23,42,0.1)',
                        }}
                    />
                    <span style={{ fontSize: '0.75rem', color: palette.textMuted, fontFamily: 'monospace' }}>
                        {color || palette.primary}
                    </span>
                </div>
            ),
        },
        {
            title: t('admin.advert.tableHeaders.active'),
            dataIndex: 'actif',
            key: 'actif',
            width: 100,
            render: (actif: boolean | undefined) => (
                <Tag
                    color={actif ? 'success' : 'default'}
                    style={{
                        borderRadius: 12,
                        padding: '4px 12px',
                        fontWeight: 600,
                        border: 'none',
                        boxShadow: actif ? '0 4px 12px rgba(139,190,30,0.2)' : '0 2px 8px rgba(15,23,42,0.06)',
                    }}
                >
                    {actif ? t('admin.advert.status.yes') : t('admin.advert.status.no')}
                </Tag>
            ),
        },
        {
            title: t('admin.advert.tableHeaders.actions'),
            key: 'actions',
            fixed: 'right',
            width: 180,
            render: (_, record: TAnnonce) => (
                <Space size="small">
                    {loadingRow === record.id ? (
                        <Spin size="small" />
                    ) : (
                        <>
                            <Button
                                icon={<EditOutlined />}
                                onClick={() => handleEditAnnonce(record)}
                                size="small"
                                style={{
                                    borderRadius: 10,
                                    border: `1px solid ${palette.border}`,
                                    boxShadow: '0 2px 8px rgba(15,23,42,0.06)',
                                }}
                            />
                            <Button
                                icon={<UploadOutlined />}
                                onClick={() => uploadImage(record)}
                                size="small"
                                style={{
                                    borderRadius: 10,
                                    border: `1px solid ${palette.border}`,
                                    boxShadow: '0 2px 8px rgba(15,23,42,0.06)',
                                }}
                            />
                            <Button
                                icon={<DeleteOutlined />}
                                danger
                                onClick={() => confirmDelete(record.id)}
                                size="small"
                                style={{
                                    borderRadius: 10,
                                    boxShadow: '0 2px 8px rgba(239,68,68,0.15)',
                                }}
                            />
                        </>
                    )}
                </Space>
            ),
        },
    ];

    const handleBackNavigation = () => {
        navigate(-1);
    };

    return (
        <AppLayout 
            title="Liste des annonces" 
            loading={loading}
            onBackNavigation={handleBackNavigation}
            showBackButton={true}
        >
            <div
                style={{
                    padding: 24,
                    borderRadius: 24,
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(244,248,251,0.9))',
                    border: `1px solid ${palette.border}`,
                    boxShadow: '0 20px 45px rgba(15,23,42,0.08)',
                    marginBottom: 24,
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                    <Title level={4} style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: palette.textPrimary }}>
                        Annonces
                    </Title>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleCreateAnnonce}
                        size="large"
                        style={{
                            borderRadius: 14,
                            background: `linear-gradient(135deg, ${palette.primary}, ${palette.secondary})`,
                            border: 'none',
                            padding: '8px 24px',
                            height: 'auto',
                            fontWeight: 600,
                            boxShadow: '0 8px 20px rgba(41,146,191,0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                        }}
                    >
                        {t('admin.advert.createButton')}
                    </Button>
                </div>

                <div className="annonce-table-wrapper">
                    <Table<TAnnonce>
                        columns={columns}
                        dataSource={annonces}
                        rowKey="id"
                        loading={loading}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total) => `${total} annonce${total > 1 ? 's' : ''}`,
                            style: {
                                marginTop: 20,
                                padding: '12px 16px',
                                borderRadius: 14,
                                background: 'rgba(255,255,255,0.6)',
                            },
                        }}
                        scroll={{ x: 1000 }}
                        rowClassName={(record, index) =>
                            index % 2 === 0 ? 'table-row-even' : 'table-row-odd'
                        }
                        style={{
                            background: 'transparent',
                        }}
                    />
                </div>
            </div>

            <AnnonceBannerForm
                visible={isModalVisible}
                onClose={() => setIsModalVisible(false)}
                annonce={currentAnnonce}
                setAnnonces={setAnnonces}
                isEditMode={isEditMode}
            />

            <ConfirmationDialog
                visible={isDeleteDialogVisible}
                onConfirm={handleDelete}
                onCancel={() => setIsDeleteDialogVisible(false)}
                loading={deleteLoading}
                title={t('admin.advert.deleteDialog.title')}
                description={t('admin.advert.deleteDialog.description')}
            />
        </AppLayout>
    );
};

export default AnnonceList;
